import withCalendar, {Calendar, WithCalendarRef} from "@COMPONENTS/hoc/withCalendar";
import {SelectBox, SelectLabel, SelectOptions, SelectWrap} from "@FRAMEWORK/ui/desktop/select/";
import {GoodsCalendar} from "@QUERIES/prdt/goods/useGoodsCompare";
import dayjs, {Dayjs} from "dayjs";
import {useCallback, useEffect, useId, useMemo, useState} from "react";
import styled, {css} from "styled-components";

interface DayProps {
	isActive?: boolean;
	CheckedDay?: boolean;
	today?: boolean;
	holiday?: boolean;
	types?: "basic" | "filter";
	rev?: "can" | "wait" | "end" | "";
}

const CalendarWrap = styled.div<{types?: "basic" | "filter"}>`
	${props => {
		if (props.types === "filter") {
			return css`
				position: absolute;
				left: 0;
				bottom: -304px;
				z-index: 10;
				border: 1px solid var(--emp-color);
				overflow: hidden;
			`;
		}
		if (props.types === "basic") {
			return css`
				position: relative;
				width: 245px;
				height: 330px;
				margin: 0 auto;
			`;
		}
	}}
`;

const CalendarBox = styled.div<DayProps>`
	width: 245px;
	background-color: #fff;
	${props =>
		props.types === "basic" &&
		css`
			table {
				th {
					height: 35px;
					background-color: #fff;
					text-align: center;
					&:not(:last-child),
					&:last-child {
						border: none;
					}
				}
				td {
					height: 35px;
					padding: 0;
					&:not(:last-child),
					&:last-child {
						border: none;
					}
				}
			}
		`}
`;

const CalendarMonth = styled.div`
	position: relative;
	display: flex;
	width: 100%;
	height: 50px;
	justify-content: center;
	em {
		font-size: 22px;
		font-weight: bold;
		color: var(--emp-color);
		line-height: 1;
	}
	label {
		font-size: var(--medium-size);
		font-weight: normal;
	}
	ul {
		top: 50px;
		left: 50%;
		width: 245px;
		max-height: 155px;
		transform: translateX(-50%);
		> li {
			justify-content: center;
			padding: 0;
		}
	}
`;

const MonthButton = styled.button<{types?: "prev" | "next"}>`
	position: absolute;
	top: 23px;
	width: 8px;
	height: 8px;
	border-left: 1px solid var(--emp-color);
	border-bottom: 1px solid var(--emp-color);
	background-color: transparent;

	&:disabled {
		border-color: var(--bl-color);
	}

	${props =>
		props.types === "prev"
			? `
                left: 10px;
                transform: rotate(45deg);
            `
			: `
                right: 10px;
                transform: rotate(-135deg);
            `}
`;

const TableCalendar = styled.table`
	width: 245px;
	table-layout: fixed;
`;

const Month = styled.th<DayProps>`
	height: 35px;
	padding: 10px;
	box-sizing: border-box;
	border-top: 1px solid var(--line-color);
	font-size: var(--basic-size);
	color: var(--emp-color);
	text-align: center;
	&:first-child {
		color: #ff5c46;
	}
	&:last-child {
		color: #4567ad;
	}
	${props => {
		if (props.types === "filter") {
			return css`
				font-size: var(--basic-size);
			`;
		}
		if (props.types === "basic") {
			return css`
				font-size: 13px;
			`;
		}
	}}
`;

const ProductDays = styled.td<DayProps>`
	position: relative;
	box-sizing: border-box;
	height: 35px;
	color: ${props => (props.isActive ? "var(--emp-color)" : "#aaa")};
	vertical-align: top;
	text-align: left;
	cursor: ${props => (props.isActive ? "pointer" : "initial")};

	${props => {
		if (props.types === "filter") {
			return css`
				> span {
					font-size: var(--basic-size);
				}
				&:hover {
					> span {
						border-color: var(--emp-color);
					}
				}
				&:first-child {
					> span {
						color: ${props.isActive ? "var(--red-color)" : "#ffc8c3"};
					}
				}
				&:last-child {
					> span {
						color: ${props.isActive ? "var(--blue-color)" : "#b5c2de"};
					}
				}
			`;
		}
		if (props.types === "basic") {
			return css`
				&:first-child {
					> span {
						color: ${props.rev ? "var(--red-color)" : "#ffc8c3"};
					}
				}
				&:last-child {
					> span {
						color: ${props.rev ? "var(--blue-color)" : "#b5c2de"};
					}
				}
				> span {
					font-size: 13px;
				}
				${props.rev
					? css`
							&:hover {
								cursor: pointer;
								> span {
									border-color: var(--emp-color);
								}
							}
					  `
					: ""}
			`;
		}
	}}
	> span {
		border: 1px solid transparent;
		border-color: ${props => (props.CheckedDay ? "var(--emp-color)" : "transparent")};
		box-sizing: border-box;
		${props => {
			if (props.types === "basic" && props.rev === "can") {
				return css`
					background-color: var(--yellow-color);
					color: var(--emp-color);
					${props.isActive && `border-color: var(--emp-color)`}
				`;
			}
			if (props.types === "basic" && props.rev === "end") {
				return css`
					background-color: var(--line-color);
					color: var(--emp-color);
				`;
			}
			if (props.types === "basic" && props.rev === "wait") {
				return css`
					background-color: #a4bdf0;
					color: var(--emp-color);
				`;
			}
		}}
	}
`;

const Day = styled.span<DayProps>`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
	z-index: 1;
	color: ${props => (props.holiday ? "var(--red-color)" : "")};
	&:before {
		position: absolute;
		top: 0;
		left: 50%;
		display: ${props => (props.today ? "block" : "none")};
		width: 5px;
		height: 5px;
		border-radius: 100%;
		background-color: var(--emp-color);
		transform: translateX(-50%);
		content: "";
	}
`;

const RevList = styled.ul`
	position: absolute;
	bottom: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 245px;
	height: 35px;
	background: #f7f7f7;
`;

const RevListItem = styled.li`
	display: inline-block;
	font-size: var(--basic-size);
	color: var(--emp-color);
	letter-spacing: -0.07em;
	&::before {
		display: inline-block;
		width: 10px;
		height: 10px;
		margin-right: 5px;
		border-radius: 50%;
		content: "";
	}
	&:nth-child(1)::before {
		background-color: var(--yellow-color);
	}
	&:nth-child(2)::before {
		background-color: #a4bdf0;
	}
	&:nth-child(3)::before {
		background-color: var(--line-color);
	}
	+ li {
		margin-left: 10px;
	}
`;

type Parameter = {
	data: Array<GoodsCalendar>;
};

type CompareCal = GoodsCalendar & {day: Dayjs | null; calStatus: "can" | "wait" | "end" | ""};

const LocalGoodsCompareCalendar = withCalendar((props: Calendar & Parameter, ref: WithCalendarRef) => {
	const types = "basic";
	const today = dayjs();
	const {
		date,
		isNext,
		isPrevious,
		rangeMonth,
		// dayList,
		month,
		// min,
		// max,
		data,
		// clearDate,
		dateChange,
		monthChange,
		nextMonth,
		prevMonth,
	} = props;

	const [showMonth, setShowMonth] = useState(false);
	const id = useId();

	useEffect(() => {
		const event = e => {
			if (document.body.contains(e.target)) {
				!e.target.closest(`#selectWrap-${CSS.escape(id)}`) && setShowMonth(false);
				e.target.closest(`#options-${CSS.escape(id)}`) && setShowMonth(false);
			}
		};
		setTimeout(() => window.addEventListener("click", event));
		return () => {
			window.removeEventListener("click", event);
		};
	}, []);

	const groupByEvStartDt = useMemo(() => {
		const result = {};
		for (const item of data) {
			const evStartDt = item.evStartDt;
			if (!result[evStartDt]) {
				result[evStartDt] = [];
			}
			result[evStartDt].push(item);
		}

		return result;
	}, [data]);

	const makeCompareDayList = useCallback(
		(referenceDate: Dayjs): Array<CompareCal> => {
			const dayCount = referenceDate.date(1).daysInMonth();
			/** 1~7 월~일 */
			const startWeekDay = referenceDate.day();
			const endWeekDay = referenceDate.date(dayCount).day();
			const firstWeekEmptyArray = Array(startWeekDay).fill(null);
			const lastWeekEmptyArray = Array(6 - endWeekDay).fill(null);
			const dayArray = Array(dayCount)
				.fill(0)
				.map((o, index) => {
					let result = {};
					const day = referenceDate.add(index, "day");
					const dayFormat = day.format("YYYYMMDD");
					const groupByItem = Array.isArray(groupByEvStartDt[dayFormat]) && groupByEvStartDt[dayFormat][0];
					const calStatusArray: Array<string> = groupByEvStartDt[dayFormat]?.map(item => item.calStatus) || [];

					/* A: 예약가능, W: 예약대기, C: 예약마감 */
					let calStatus = "";
					if (calStatusArray.includes("A")) {
						calStatus = "can";
					} else if (calStatusArray.includes("W")) {
						calStatus = "wait";
					} else if (calStatusArray.includes("C")) {
						calStatus = "end";
					}

					result = {...groupByItem, day, calStatus};
					return result;
				});
			return [...firstWeekEmptyArray, ...dayArray, ...lastWeekEmptyArray];
		},
		[groupByEvStartDt],
	);

	const compareDayList = useMemo(() => {
		const days = makeCompareDayList(month.date(1));
		return Array.from({length: days.length / 7}).map(() => days.splice(0, 7));
	}, [month]);

	return (
		<CalendarWrap id={id} types={types}>
			<CalendarBox types={types}>
				<CalendarMonth>
					<SelectWrap width={90} id={`selectWrap-${id}`}>
						<SelectBox types="calendar" onClick={() => setShowMonth(!showMonth)}>
							<SelectLabel isActive>{month.format("YYYY.MM")}</SelectLabel>
						</SelectBox>
						{showMonth && (
							<SelectOptions id={`options-${id}`}>
								{rangeMonth?.map((day: Dayjs, index) => (
									<li
										value={day.format("YYYYMMDD")}
										key={index}
										onClick={e => {
											monthChange(String(e.currentTarget.value));
										}}
									>
										{day.format("YYYY.MM")}
									</li>
								))}
							</SelectOptions>
						)}
					</SelectWrap>
					{isPrevious && (
						<MonthButton types="prev" onClick={prevMonth}>
							<span>이전달</span>
						</MonthButton>
					)}
					{isNext && (
						<MonthButton types="next" onClick={nextMonth}>
							<span>다음달</span>
						</MonthButton>
					)}
				</CalendarMonth>
				<TableCalendar>
					<thead>
						<tr>
							<Month types={types}>일</Month>
							<Month types={types}>월</Month>
							<Month types={types}>화</Month>
							<Month types={types}>수</Month>
							<Month types={types}>목</Month>
							<Month types={types}>금</Month>
							<Month types={types}>토</Month>
						</tr>
					</thead>
					<tbody>
						{compareDayList?.map((week, index) => (
							<tr key={index}>
								{week.map((item, weekIdx) => {
									const day = item?.day as unknown as dayjs.Dayjs;
									// 상품 존재 여부
									const isActive = !(day < today.add(-1, "day")) && (!!item?.calStatus as boolean);
									return (
										<ProductDays
											types={types}
											onClick={e => {
												if (isActive) {
													dateChange(day, e);
												}
											}}
											rev={item?.calStatus || ""}
											key={weekIdx}
											CheckedDay={date && date.isSame(day)}
										>
											<Day
												data-goods-cd={item?.goodsCd}
												data-dsp-sid={item?.dspSid}
												data-date={item?.evStartDt}
												data-single-type-cd={item?.singleTypeCd}
												types={types}
												today={day && today.isSame(day, "day")}
											>
												{day && day.format("DD")}
											</Day>
										</ProductDays>
									);
								})}
							</tr>
						))}
					</tbody>
				</TableCalendar>
				<RevList>
					<RevListItem>예약가능</RevListItem>
					<RevListItem>예약대기</RevListItem>
					<RevListItem>예약마감</RevListItem>
				</RevList>
			</CalendarBox>
		</CalendarWrap>
	);
});

export default LocalGoodsCompareCalendar;
